import { useCallback, useEffect, useRef } from "react";
import { VivoPlot } from "./index";
import { VivoPlotWrapperProps } from "./types";
import { prepareDashboardPostRequestBody } from "@/components/UI/TableToolbar/Filtering/filtersApiUtil";
import {
  PLOT_DATA_PAGE_SIZE,
  usePaginatedPlotFetch,
} from "@/hooks/usePaginatedPlotFetch";
import { PlotConflictWarning } from "./components/PlotConflictWarning";
import { PlotControls } from "./components/PlotControls";
import { detectFilterConflicts } from "./utils/filterConflictUtils";
import { MetricCardVisualization } from "./MetricCard";
import { LineChartVisualization } from "./LineChart";

export const VivoPlotWrapper = ({
  inModal,
  data: dataFromProps,
  type,
  x,
  y,
  subgroup,
  fill,
  config,
  deletePlot,
  hidePlot,
  editPlot,
  duplicatePlot,
  onConfigChange,
  globalFilters,
  plotFilters,
  endpointSuffix,
  endpointPrefix,
  selectedIdName,
  selectedIdValue,
  setSelectedId,
  onElementSelect,
  filterableColumns,
  appliedFilters,
  onPointActivate,
  metadata,
  columnsTitleCreator,
  datasetName,
  subjectId,
  onPlotMetadataChange,
}: VivoPlotWrapperProps) => {
  const plotClassName = config.plotId;
  const plotTitle = config?.labels?.title || config.plotId || "Untitled Plot";
  const hasPlotFilters = plotFilters && Object.keys(plotFilters).length > 0;
  const editPlotRef = useRef(editPlot);
  const runtimeContextRef = useRef({
    dataFromProps,
    data: undefined,
    type,
    x,
    y,
    subgroup,
    fill,
    config,
    plotFilters,
    metadata,
    columnsTitleCreator,
  });

  useEffect(() => {
    editPlotRef.current = editPlot;
  }, [editPlot]);

  useEffect(() => {
    runtimeContextRef.current = {
      ...runtimeContextRef.current,
      dataFromProps,
      type,
      x,
      y,
      subgroup,
      fill,
      config,
      plotFilters,
      metadata,
      columnsTitleCreator,
    };
  }, [
    dataFromProps,
    type,
    x,
    y,
    subgroup,
    fill,
    config,
    plotFilters,
    metadata,
    columnsTitleCreator,
  ]);

  const openPlotEditor = useCallback((openToDataTab: boolean) => {
    const {
      dataFromProps: currentDataFromProps,
      data: currentData,
      type: currentType,
      x: currentX,
      y: currentY,
      subgroup: currentSubgroup,
      fill: currentFill,
      config: currentConfig,
      plotFilters: currentPlotFilters,
      metadata: currentMetadata,
      columnsTitleCreator: currentColumnsTitleCreator,
    } = runtimeContextRef.current;

    editPlotRef.current?.({
      data: currentDataFromProps || (currentData as any)?.results,
      type: currentType,
      x: currentX,
      y: currentY,
      subgroup: currentSubgroup,
      fill: currentFill,
      config: {
        ...currentConfig,
        plotFilters: currentPlotFilters,
        metadata: currentMetadata,
        columnsTitleCreator: currentColumnsTitleCreator,
      },
      dataIsLoading: false,
      plotFilters: currentPlotFilters,
      openToDataTab,
    });
  }, []);

  const filterClickHandler = useCallback(
    () => openPlotEditor(true),
    [openPlotEditor],
  );

  const runtimeConfig = {
    ...config,
    selectedIdName,
    selectedIdValue,
    setClickedSubject: setSelectedId,
    onPointActivate,
    hasPlotFilters,
    ...(onElementSelect
      ? { onElementSelect, filterableColumns, appliedFilters }
      : {}),
    ...(hasPlotFilters
      ? {
          plotFilters,
          metadata,
          columnsTitleCreator,
          ...(editPlot
            ? {
                filterClickHandler,
              }
            : {}),
        }
      : {}),
  };

  const conflictInfo = detectFilterConflicts(globalFilters, plotFilters);
  const globalFiltersForRequest = subjectId
    ? { ...globalFilters, subject: [subjectId] }
    : globalFilters;

  const normalizedEndpointSuffix = endpointSuffix?.includes("?")
    ? endpointSuffix
    : endpointSuffix?.endsWith("/")
      ? endpointSuffix
      : `${endpointSuffix}/`;

  const payload = {
    dataset_type: datasetName,
    ...prepareDashboardPostRequestBody({
      page: 1,
      pageSize: PLOT_DATA_PAGE_SIZE,
      global_filters: globalFiltersForRequest,
      plot_filters: plotFilters,
      allFiltersData: metadata?.filters,
    }),
  };

  const { data, isFetching, isLoading } = usePaginatedPlotFetch({
    endpointPrefix,
    endpointSuffix: normalizedEndpointSuffix,
    body: payload,
    enabled: !dataFromProps,
  });

  // Denominator fetch for the metric card's donut display style.
  // "total" → re-fetch without plot_filters; "filter" → fetch with denominatorFilter.
  const mc = config.metricCard;
  const needsDenominatorFetch =
    type === "metric-card" &&
    mc?.displayStyle === "donut" &&
    mc.denominatorSource !== "fixed";

  const denominatorPlotFilters =
    mc?.denominatorSource === "filter" ? (mc.denominatorFilter ?? {}) : {};

  const denominatorPayload = {
    dataset_type: datasetName,
    ...prepareDashboardPostRequestBody({
      page: 1,
      pageSize: PLOT_DATA_PAGE_SIZE,
      global_filters: globalFiltersForRequest,
      plot_filters: denominatorPlotFilters,
      allFiltersData: metadata?.filters,
    }),
  };

  const { data: denominatorData, isFetching: denominatorIsFetching } =
    usePaginatedPlotFetch({
      endpointPrefix,
      endpointSuffix: normalizedEndpointSuffix,
      body: denominatorPayload,
      enabled: needsDenominatorFetch,
    });

  useEffect(() => {
    runtimeContextRef.current = {
      ...runtimeContextRef.current,
      data,
    };
  }, [data]);

  const rows = (dataFromProps || (data as any)?.results) ?? [];
  const denominatorRows: Record<string, unknown>[] | null =
    needsDenominatorFetch ? ((denominatorData as any)?.results ?? null) : null;

  return (
    <div
      className={`bg-white rounded-lg shadow-omni relative ${plotClassName} flex flex-col justify-start overflow-hidden ${type === "metric-card" || type === "line" ? "items-stretch min-h-[240px]" : "items-start"} ${inModal ? "" : "h-full"}`}
      data-has-filters={hasPlotFilters}
      data-plot-id={config.plotId}
      data-plot-title={plotTitle}
    >
      {/* Warning overlay for conflicting filters */}
      {conflictInfo.hasConflicts && (
        <PlotConflictWarning
          conflicts={conflictInfo.conflicts}
          columnsTitleCreator={columnsTitleCreator}
        />
      )}

      {/* MetricCardVisualization renders no controls of its own, so the wrapper
          overlays them; LineChartVisualization renders its own controls. */}
      {type === "metric-card" &&
        !inModal &&
        (deletePlot || hidePlot || editPlot) && (
          <div className="absolute top-2 right-2 z-10">
            <PlotControls
              inModal={false}
              showLegend={false}
              setShowLegend={() => {}}
              fillScale={null}
              editThisPlot={() => openPlotEditor(false)}
              hidePlot={hidePlot ?? (() => {})}
              deletePlot={deletePlot ?? (() => {})}
              duplicatePlot={duplicatePlot ?? null}
              plotClassName={plotClassName ?? ""}
              config={config as any}
            />
          </div>
        )}

      {type === "metric-card" ? (
        <MetricCardVisualization
          rows={rows}
          denominatorRows={denominatorRows}
          config={runtimeConfig}
          isLoading={isLoading || isFetching || denominatorIsFetching}
        />
      ) : type === "line" ? (
        <LineChartVisualization
          rows={rows}
          config={runtimeConfig}
          isLoading={isLoading || isFetching}
          deletePlot={deletePlot}
          hidePlot={hidePlot}
          editPlot={editPlot ? () => openPlotEditor(false) : undefined}
        />
      ) : (
        <VivoPlot
          inModal={inModal}
          data={rows}
          type={type}
          x={x}
          y={y}
          subgroup={subgroup}
          fill={fill}
          config={runtimeConfig}
          selectedIdName={selectedIdName}
          selectedIdValue={selectedIdValue}
          setSelectedId={setSelectedId}
          dataIsLoading={isLoading || isFetching}
          deletePlot={deletePlot}
          hidePlot={hidePlot}
          editPlot={editPlot}
          duplicatePlot={duplicatePlot}
          onConfigChange={onConfigChange}
          plotFilters={plotFilters}
          onPlotMetadataChange={onPlotMetadataChange}
        />
      )}
    </div>
  );
};
